import type { ReactNode } from 'react'
import { forwardRef, useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { DAY_MONTH_NAME } from 'constants/dateFormats'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import OfferFilterDropdownBox from 'components/OfferPage/Filters/OfferFilterDropdownBox'
import moment from 'moment'

const SizedFilterSelectBox = styled(OfferFilterDropdownBox)`
  ${mediaQueryUp.desktop} {
    min-width: ${rem(250)};
  }
`

interface Props {
  checkInDate?: string
  checkOutDate?: string
  isOpen: boolean
  inSidebar?: boolean
}

const CalendarDatePickerSelectBox = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    checkInDate,
    checkOutDate,
    isOpen,
    inSidebar,
  } = props
  const intl = useIntl()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const checkIn = useMemo(() => checkInDate ? moment(checkInDate) : undefined, [checkInDate])
  const checkOut = useMemo(() => checkOutDate ? moment(checkOutDate) : undefined, [checkOutDate])
  const selectedDuration = checkOut?.diff(checkIn, 'days')
  const areDatesValid = checkIn && checkOut
  const dispatch = useOfferPageDispatch()

  const dateRangeValue = selectedDuration && areDatesValid
    ? intl.formatMessage(
        {
          id: 'WiO4V8RKVJxj03dkt73e',
          defaultMessage: '<b>{checkIn} - {checkOut}</b> ({nightCount, plural, one {# night} other {# nights}})',
          description: 'Selected date range with night count shown on the bedbank date picker',
        },
        {
          b: (...chunks: Array<ReactNode>) => <b>{chunks}</b>,
          checkIn: checkIn.format(DAY_MONTH_NAME),
          checkOut: checkOut.format(DAY_MONTH_NAME),
          nightCount: selectedDuration,
        },
      )
    : undefined

  const onClick = useCallback(() => {
    if (isOpen) {
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
    }
  }, [dispatch, isOpen, trackEvent])

  return (
    <SizedFilterSelectBox
      onClick={onClick}
      active={isOpen}
      ref={ref}
      id="date-picker"
      data-testid="CalendarDatePickerSelectBox"
      icon={<LineCalendarIcon />}
      inSidebar={inSidebar}
      label={intl.formatMessage({
        id: 'Z3tV-RHO9HjkxA2QLDom',
        defaultMessage: 'When',
        description: 'Label for the date picker filter on bedbank offer page',
      })}
      placeholder={intl.formatMessage({
        id: 'pWZ4-L5dO9jnbw7gcPQC',
        defaultMessage: 'Select your dates',
        description: 'Placeholder text for the date picker filter on bedbank offer page',
      })}
      value={dateRangeValue}
    />
  )
})

CalendarDatePickerSelectBox.displayName = 'CalendarDatePickerSelectBox'

export default withOfferPageState(state => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  isOpen: state.activeFilter === 'checkin',
}), CalendarDatePickerSelectBox)
